import { ChangeEvent, useState, useEffect, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { useHistory, useLocation } from 'react-router';

import { KnmErknmStatusIdsByCodeEnum } from 'APIPackage/CatalogsModule/enums/KnmErknmStatusIdsByCodeEnum';
import { KnmTypeIdsByCodeEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeIdsByCodeEnum';
import { SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { KnmRemoveFromPlanModal } from 'CorePackage/MainModule/components/KnmRemoveFromPlanModal';
import { ROUTE_KNM_NEW } from 'CorePackage/MainModule/routes/routes';
import {
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
  getUserRoleIsOmbudsman,
  getUserProfileIsTest,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { SearchInput } from 'UIPackage/MainModule/components/SearchInput';
import { FilterCheckbox } from 'UIPackage/MainModule/components/FilterCheckbox';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { Button } from 'UIPackage/MainModule/components/Button';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { IKnmMoveToPlanAttachment } from 'CorePackage/MainModule/interfaces/IKnmMoveToPlanAttachment';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { compareDates, formatDate, getFormatedClientTime } from 'CorePackage/MainModule/common/formatDate';
import {
  knmListCancelKnms,
  knmListDisapproveKnms,
  knmListGetData,
  knmListRemoveKnmsFromPlan,
  knmListSetSearchValue,
  knmListUpdateKnmsDuration,
  knmListUpdateKnmsStartDate,
} from 'KnmListPackage/MainModule/actions/knmListActions';
import { getKnmListRejected, getKnmListSearchValue } from 'KnmListPackage/MainModule/selectors/knmListSelectors';
import { knmListFilterSetSwitcher } from 'KnmListPackage/KnmListFiltersModule/actions/knmListFilterActions';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnmExclusionReasonSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmExclusionReasonSelectOption';
import { getStorageItem } from 'CorePackage/MainModule/common/localStore';
import { StorageNamesEnum } from 'CorePackage/MainModule/enums/StorageNamesEnum';
import { TableCheckItems } from 'CorePackage/MainModule/types/TableCheckItems';
import { IKnmRaw } from 'KnmListPackage/MainModule/interfaces/IKnmRaw';
import { ITableColumn } from 'CorePackage/TableModule/interfaces/ITableColumn';
import { IKnmLocationState } from 'KnmListPackage/MainModule/interfaces/IKnmLocationState';

import { CancelKnmsModal } from './CancelKnmsModal';
import { DisapproveKnmsModal, IDisapproveKnmsModalProps } from './DisapproveKnmsModal';
import { UpdateKnmsDurationModal, IUpdateKnmsDurationModalProps } from './UpdateKnmsDurationModal';
import { UpdateKnmsStartDateModal, IUpdateKnmsStartDateModalProps } from './UpdateKnmsStartDateModal';
import styles from './KnmListPanel.module.scss';
import { KnmListFiltersModal } from '../../KnmListFiltersModule/components/KnmListFiltersModal';
import { KnmListDropdown } from './KnmListDropdown';

const findDisabledSettings = (settings: IAdminSetting[], code: AdminSettingCodesEnum) => {
  const currentDate = getFormatedClientTime();

  return settings
    .filter(item => item.code === code)
    .reduce((isDisable, currentSettings) => {
      if (currentSettings.value) {
        const resultCompare = compareDates({ dateLeft: currentSettings.value, dateRight: currentDate });

        return resultCompare === 0 || resultCompare === 1;
      }
      if (currentSettings.periods && !isDisable) {
        return !!currentSettings.periods.find(item => {
          const compareStart = compareDates({ dateLeft: currentDate, dateRight: item.startDate });
          const compareEnd = compareDates({ dateLeft: currentDate, dateRight: item.startDate });

          return (compareStart === 0 || compareStart === 1) && (compareEnd === 0 || compareEnd === -1);
        });
      }

      return isDisable;
    }, false);
};

interface IKnmListPanelProps {
  dataCount: number;
  columns: ITableColumn<IKnmRaw>[];
  onDeleteClick: () => void;
  onRestoreClick: () => void;
  itemsCheck: TableCheckItems;
  knms: IKnmRaw[];
  isErknm: boolean;
  isProsecutorOrSuperProsecutor: boolean;
  isSupervisor: boolean;
  isOmbudsman: boolean;
  searchInputValue: string;
  rejectedCount: number;
  settings: IAdminSetting[];
  isTestUser: boolean;
}

const KnmListPanelWithoutStore = ({
  dataCount,
  columns,
  onDeleteClick,
  onRestoreClick,
  itemsCheck,
  knms,
  isErknm,
  isProsecutorOrSuperProsecutor,
  isSupervisor,
  isOmbudsman,
  searchInputValue,
  rejectedCount,
  settings,
  isTestUser,
}: IKnmListPanelProps) => {
  const isFilterCheckboxActive = !!getStorageItem(StorageNamesEnum.KNM_FILTER_ON);
  const selectedKnmsIds = Object.keys(itemsCheck).filter(key => itemsCheck[key]);
  const [isFiltersModalVisible, setIsFiltersModalVisible] = useState(false);
  const [isSearchInputValid, setIsSearchInputValid] = useState(false);
  const [searchInputError, setSearchInputError] = useState('');
  const history = useHistory();
  const location = useLocation<IKnmLocationState>();
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [isDisabledExport, setIsDisabledExport] = useState(false);

  useEffect(() => {
    if (settings.length) {
      setIsDisabledExport(findDisabledSettings(settings, AdminSettingCodesEnum.KNM_MATCH_EXPORT_DATE));
    }
  }, [settings]);

  const isSomeSelectedKnmDeleted = selectedKnmsIds.some(knmId =>
    Boolean(knms.find(knm => knmId === String(knm.id))?.deleted),
  );

  const selectedKnmTypeIds = selectedKnmsIds.reduce(
    (uniqueKnmTypeIds: Array<IKnmRaw['knmTypeId'] | undefined>, knmId) => {
      const knmTypeId = knms.find(knm => knmId === String(knm.id))?.knmTypeId;

      if (typeof knmTypeId === 'number' && !uniqueKnmTypeIds.includes(knmTypeId)) {
        uniqueKnmTypeIds.push(knmTypeId);
      }

      return uniqueKnmTypeIds;
    },
    [],
  );

  const isSomeSelectedKnmTypePlan = selectedKnmsIds.some(knmId => {
    const currentKnm = knms.find(knm => knmId === String(knm.id));

    return Boolean(currentKnm?.knmTypeId === KnmTypeIdsByCodeEnum.PP && currentKnm.planId);
  });

  const isSomeSelectedKnmCreatedByErpDb = selectedKnmsIds.some(knmId =>
    Boolean(knms.find(knm => knmId === String(knm.id))?.createdByErpDb),
  );

  const isEverySelectedKnmInCorrectStatus = selectedKnmsIds.every(knmId => {
    const currentKnm = knms.find(knm => knmId === String(knm.id));

    return (
      currentKnm && itemsCheck[currentKnm?.id] && currentKnm.statusId !== KnmErknmStatusIdsByCodeEnum.TYPE_COMPLETED
    );
  });

  const isErpDeleteButtonDisabled =
    selectedKnmsIds.length === 0 ||
    (selectedKnmsIds.length > 0 && isSomeSelectedKnmDeleted) ||
    isSomeSelectedKnmCreatedByErpDb ||
    (isSupervisor && isSomeSelectedKnmTypePlan) ||
    !isEverySelectedKnmInCorrectStatus;

  const isErknmDeleteButtonEnabled =
    isSupervisor &&
    selectedKnmsIds.length > 0 &&
    (isTestUser
      ? knms.every((knm: IKnmRaw) => {
          if (!itemsCheck[String(knm.id)]) {
            return true;
          }
          const { statusId, isTest } = knm;

          return (
            (statusId === KnmErknmStatusIdsByCodeEnum.FILL_IN_PROCESS ||
              statusId === KnmErknmStatusIdsByCodeEnum.READY_FOR_APPROVE) &&
            isTest
          );
        })
      : knms.every((knm: IKnmRaw) => {
          if (!itemsCheck[String(knm.id)]) {
            return true;
          }
          const { statusId } = knm;

          return (
            statusId === KnmErknmStatusIdsByCodeEnum.FILL_IN_PROCESS ||
            statusId === KnmErknmStatusIdsByCodeEnum.READY_FOR_APPROVE
          );
        }));

  const isDeleteButtonDisabled = isErknm ? !isErknmDeleteButtonEnabled : isErpDeleteButtonDisabled;

  const handleFilterCheckboxChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(knmListFilterSetSwitcher(e.target.checked));
      dispatch(knmListGetData());
    },
    [dispatch],
  );

  const handleFilterButtonClick = useCallback(() => {
    setIsFiltersModalVisible(true);
  }, []);

  const closeFiltersModal = useCallback(() => setIsFiltersModalVisible(false), []);

  const handleSearchInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      dispatch(knmListSetSearchValue(String(value)));
      const isValid = value.length > 2 || value.length === 0;

      setIsSearchInputValid(isValid);
    },
    [dispatch],
  );

  const handleSearchInputSubmit = useCallback(() => {
    if (!isSearchInputValid) {
      setSearchInputError('Длина строки должна быть не менее 3 символов');
    } else {
      setSearchInputError('');
      dispatch(knmListGetData());
    }
  }, [dispatch, isSearchInputValid]);

  const handleOnBlur = useCallback(() => {
    !isSearchInputValid ? setSearchInputError('Длина строки должна быть не менее 3 символов') : setSearchInputError('');
  }, [isSearchInputValid]);

  const handleDropdownDeleteButtonClick = useCallback(() => {
    onDeleteClick();
  }, [onDeleteClick]);

  const [isCancelKnmsModalVisible, setIsCancelKnmsModalVisible] = useState(false);
  const openCancelKnmsModal = useCallback(() => setIsCancelKnmsModalVisible(true), []);
  const closeCancelKnmsModal = useCallback(() => setIsCancelKnmsModalVisible(false), []);
  const handleCancelKnmsModalApply = useCallback(
    (commentText?: string) => {
      closeCancelKnmsModal();
      dispatch(
        knmListCancelKnms(
          selectedKnmsIds.map(knmId => Number(knmId)),
          commentText,
        ),
      );
    },
    [closeCancelKnmsModal, dispatch, selectedKnmsIds],
  );

  const handleCancelKnmsButtonClick = useCallback(() => {
    openCancelKnmsModal();
  }, [openCancelKnmsModal]);

  const [isRemoveFromPlanVisible, setIsRemoveFromPlanVisible] = useState(false);

  const openRemoveFromPlanModal = useCallback(() => setIsRemoveFromPlanVisible(true), []);

  const closeRemoveFromPlanModal = useCallback(() => setIsRemoveFromPlanVisible(false), []);

  const handleRemoveFromPlanModalApply = useCallback(
    (params?: IKnmMoveToPlanAttachment | IKnmExclusionReasonSelectOption) =>
      dispatch(knmListRemoveKnmsFromPlan(selectedKnmsIds, params)),
    [dispatch, selectedKnmsIds],
  );

  const [isDisapproveKnmsModalVisible, setIsDisapproveKnmsModalVisible] = useState(false);
  const openDisapproveKnmsModal = useCallback(() => setIsDisapproveKnmsModalVisible(true), []);
  const closeDisapproveKnmsModal = useCallback(() => setIsDisapproveKnmsModalVisible(false), []);
  const handleDisapproveKnmsModalApply: IDisapproveKnmsModalProps['onApply'] = useCallback(
    rejectReasons => {
      closeDisapproveKnmsModal();
      dispatch(
        knmListDisapproveKnms(
          selectedKnmsIds.map(knmId => Number(knmId)),
          rejectReasons.map(rejectReason => ({
            typeId: rejectReason.type?.id,
            text: rejectReason.note,
          })),
        ),
      );
    },
    [closeDisapproveKnmsModal, dispatch, selectedKnmsIds],
  );

  const [isUpdateKnmsStartDateModalVisible, setIsUpdateKnmsStartDateModalVisible] = useState(false);
  const openUpdateKnmsStartDateModal = useCallback(() => setIsUpdateKnmsStartDateModalVisible(true), []);
  const closeUpdateKnmsStartDateModal = useCallback(() => setIsUpdateKnmsStartDateModalVisible(false), []);
  const handleUpdateKnmsStartDateModalApply: IUpdateKnmsStartDateModalProps['onApply'] = useCallback(
    startDate => {
      closeUpdateKnmsStartDateModal();
      dispatch(
        knmListUpdateKnmsStartDate(
          selectedKnmsIds.map(knmId => Number(knmId)),
          formatDate({ date: startDate, dateFormat: SYSTEM_DATE_FORMAT_FNS }),
        ),
      );
    },
    [closeUpdateKnmsStartDateModal, dispatch, selectedKnmsIds],
  );

  const [isUpdateKnmsDurationModalVisible, setIsUpdateKnmsDurationModalVisible] = useState(false);
  const openUpdateKnmsDurationModal = useCallback(() => setIsUpdateKnmsDurationModalVisible(true), []);
  const closeUpdateKnmsDurationModal = useCallback(() => setIsUpdateKnmsDurationModalVisible(false), []);
  const handleUpdateKnmsDurationModalApply: IUpdateKnmsDurationModalProps['onApply'] = useCallback(
    (durationDays, durationHours) => {
      closeUpdateKnmsDurationModal();
      dispatch(
        knmListUpdateKnmsDuration(
          selectedKnmsIds.map(knmId => Number(knmId)),
          durationDays ? Number(durationDays) || undefined : undefined,
          durationHours ? Number(durationHours) || undefined : undefined,
        ),
      );
    },
    [closeUpdateKnmsDurationModal, dispatch, selectedKnmsIds],
  );

  const getPlanIdFromPathname = (pathname: string): string | undefined => pathname.match(/\/knms\/(\d+)/)?.[1];

  const handleAddKNM = useCallback(() => {
    const planId = getPlanIdFromPathname(location.pathname);

    history.push({
      pathname: ROUTE_KNM_NEW,
      state: { planId },
    });
  }, [history, location.pathname]);

  const filterButtonAttributes = {
    ...(isFilterCheckboxActive && { 'data-active': true }),
  };

  return (
    <div className="shared-panel-container">
      <div className={styles.Actions}>
        {isOmbudsman || isProsecutorOrSuperProsecutor ? null : (
          <>
            <Button startIcon={styles.AddIcon} text="Добавить" id="addButton" color="primary" onClick={handleAddKNM} />
            <Button
              className={styles.ButtonDelete}
              startIcon={styles.DeleteIcon}
              onClick={onDeleteClick}
              disabled={isDeleteButtonDisabled}
              text="Удалить"
              id="deleteButton"
            />
          </>
        )}
        <SearchInput
          className={styles.SearchInput}
          value={searchInputValue}
          name="searchString"
          placeholder="Поиск КНМ"
          buttonDisabled={!isSearchInputValid}
          error={searchInputError}
          onChange={handleSearchInputChange}
          onSubmit={handleSearchInputSubmit}
          onBlur={handleOnBlur}
        />
        <Tooltip
          placement="right"
          content={
            <div className={styles.TooltipContent}>
              Доступен поиск КНМ по следующим критериям:
              <ul>
                <li>Учетный номер КНМ</li>
                <li>Номер плана КНМ</li>
                <li>
                  Дата и номер распоряжения или приказа <br /> руководителя органа контроля о проведении КНМ
                </li>
                <li>ИНН или ОГРН проверяемого лица</li>
                <li>Наименование проверяемого лица</li>
                <li>Дата начала проведения КНМ</li>
              </ul>
            </div>
          }
        >
          <span className={styles.TooltipIcon} />
        </Tooltip>
      </div>
      <div className={styles.Filters}>
        <p className={styles.CurrentFilter}>
          {isErknm ? 'КНМ моей прокуратуры' : 'Проверки моей прокуратуры'}&nbsp;
          <small className={styles.CurrentFilterNumbers}>
            ({dataCount}/{rejectedCount})
          </small>
        </p>
        <FilterCheckbox
          className={styles.FilterCheckbox}
          id="filterToggler"
          name="filterToggler"
          checked={isFilterCheckboxActive}
          onChange={handleFilterCheckboxChange}
        />
        <Button
          className={styles.FilterButton}
          text="Фильтр..."
          color="info"
          onClick={handleFilterButtonClick}
          {...filterButtonAttributes}
        />
        <KnmListDropdown
          dataCount={dataCount}
          columns={columns}
          onRestoreClick={onRestoreClick}
          knms={knms}
          selectedKnmsIds={selectedKnmsIds}
          isDeleteButtonDisabled={isDeleteButtonDisabled}
          disabledExport={isDisabledExport}
          handleDropdownDeleteButtonClick={handleDropdownDeleteButtonClick}
          openRemoveFromPlanModal={openRemoveFromPlanModal}
          openUpdateKnmsStartDateModal={openUpdateKnmsStartDateModal}
          openUpdateKnmsDurationModal={openUpdateKnmsDurationModal}
          openDisapproveKnmsModal={openDisapproveKnmsModal}
          handleCancelKnmsButtonClick={handleCancelKnmsButtonClick}
        />
      </div>
      {isFiltersModalVisible && <KnmListFiltersModal visible={isFiltersModalVisible} onClose={closeFiltersModal} />}
      <CancelKnmsModal
        visible={isCancelKnmsModalVisible}
        knmsNumber={selectedKnmsIds.length}
        onApply={handleCancelKnmsModalApply}
        onClose={closeCancelKnmsModal}
      />
      <DisapproveKnmsModal
        visible={isDisapproveKnmsModalVisible}
        knmTypeId={selectedKnmTypeIds[0]}
        knmsNumber={selectedKnmsIds.length}
        onApply={handleDisapproveKnmsModalApply}
        onClose={closeDisapproveKnmsModal}
      />
      <UpdateKnmsStartDateModal
        knms={knms}
        selectedKnmsIds={selectedKnmsIds}
        visible={isUpdateKnmsStartDateModalVisible}
        onApply={handleUpdateKnmsStartDateModalApply}
        onClose={closeUpdateKnmsStartDateModal}
      />
      <UpdateKnmsDurationModal
        visible={isUpdateKnmsDurationModalVisible}
        knmsNumber={selectedKnmsIds.length}
        onApply={handleUpdateKnmsDurationModalApply}
        onClose={closeUpdateKnmsDurationModal}
      />
      <KnmRemoveFromPlanModal
        visible={isRemoveFromPlanVisible}
        knmIds={selectedKnmsIds}
        isErknm={isErknm}
        onApply={handleRemoveFromPlanModalApply}
        onClose={closeRemoveFromPlanModal}
      />
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  isErknm: getLawInterface(state) === LawInterfacesEnum.FZ_248,
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  isOmbudsman: getUserRoleIsOmbudsman(state),
  searchInputValue: getKnmListSearchValue(state),
  rejectedCount: getKnmListRejected(state),
  settings: getAdminFetchSettings(state),
  isTestUser: getUserProfileIsTest(state),
});

export const KnmListPanel = connect(mapStateToProps)(KnmListPanelWithoutStore);
